import { mdiBank, mdiMapMarker, mdiAlertCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { CustomerDetailModel } from "../../../../service/models/orders/customer_detail_model";
import ModalDialog from "../../../../ui/new_components/common/modal";
import AddBillingWidget from "./AddBilling";
import {
  AddressType,
  CustomerBillingAddressModel,
  RegisteredofficeAddressModel,
} from "../../../../context_providers/auth/user_model";
import { GetBillingProfilesOfUserAndTypeApi } from "../../../../service/repos/customer/billing_profile_repo";
import { fetchDelete } from "../../../../service/api_client";

interface NewBillingProfileWidgetProps {
  registered_address?: RegisteredofficeAddressModel; // Consider specifying a proper type instead of `any`
  uid?: string;
  type: string;
  onBillingAddressAdded?: (address: CustomerBillingAddressModel) => void;
  onBillingAddressUpdated?: (address: CustomerBillingAddressModel) => void;
  onBillingAddressDeleted?: (address: CustomerBillingAddressModel) => void;
  setRegisteredAddress: (data: RegisteredofficeAddressModel) => void;
  viewOnly?: boolean;
  addOpen?: boolean;
  onAddOpenChange?: (open: boolean) => void;
  id_prefix?: string;
}
const NewBillingWidget: FunctionComponent<NewBillingProfileWidgetProps> = ({
  registered_address,
  uid,
  type,
  onBillingAddressAdded,
  onBillingAddressUpdated,
  onBillingAddressDeleted,
  setRegisteredAddress,
  viewOnly = false,
  addOpen,
  onAddOpenChange,
  id_prefix = "billing-address",
}) => {
  const { showToast } = useToast();
  const [toDelete, setToDelete] = useState<CustomerDetailModel | null>(null);
  const register_address = registered_address;
  const [addNewInternal, setAddNewInternal] = useState(false);
  const isAddControlled = onAddOpenChange !== undefined;
  const addNew = isAddControlled ? !!addOpen : addNewInternal;
  const setAddNew = (v: boolean) =>
    isAddControlled ? onAddOpenChange!(v) : setAddNewInternal(v);
  const [edit, setEdit] = useState(false);
  const [editProfile, setEditProfile] = useState<CustomerDetailModel | null>(
    null,
  );
  const [customers, setCustomers] = useState<CustomerDetailModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (uid && !customers.length) {
      getCustomers();
    }
  }, [uid]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const getCustomers = async () => {
    setLoading(true);
    const res = await GetBillingProfilesOfUserAndTypeApi(uid, type);

    if (res.success) {
      setCustomers(res.data.billing_profiles);
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };

  const handleDelete = async (profile: CustomerDetailModel) => {
    if (uid && profile.id) {
      setLoading(true);
      const res = await fetchDelete(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/users/customer/${uid}/billing/${profile.id}`,
      );
      setLoading(false);
      if (!res.success) {
        showToast({ type: ToastType.error, text: res.error });
        return;
      }
      showToast({ type: ToastType.success, text: res.message });
    }

    setCustomers((prev) =>
      prev.filter((c) =>
        profile.id
          ? c.id !== profile.id
          : (c.branch ?? "").trim().toLowerCase() !==
            (profile.branch ?? "").trim().toLowerCase(),
      ),
    );

    if (onBillingAddressDeleted) {
      onBillingAddressDeleted({
        branch_name: profile.branch,
        type: AddressType.billing,
        line1: profile.address?.line1 ?? "",
        line2: profile.address?.line2 ?? "",
        line3: profile.address?.line3 ?? "",
        city: profile.address?.city ?? "",
        state: profile.address?.state ?? "",
        country: profile.address?.country ?? "",
        pincode: profile.address?.pincode ?? 0,
      });
    }
  };

  const l = page * 2 - 1;
  const h = l + 2 - 1;

  const renderCardBody = (customer: CustomerDetailModel) => (
    <>
      <div className="flex gap-2 my-1">
        <span>
          <Icon path={mdiBank} className="h-4 w-4 text-gray-500" />
        </span>
        <p>
          <span className="font-semibold">Branch Name: </span>
          <span>{customer?.branch}</span>
        </p>
      </div>

      <hr className="my-2" />
      <div className="flex gap-1">
        <span className="font-semibold"> Country :</span>
        <p>{customer.address?.country ?? "--"}</p>
      </div>
      <div>
        <span className="font-semibold"> State : </span>
        <span>{customer.address?.state ?? "--"}</span>
      </div>
      <div>
        <span className="font-semibold"> City : </span>
        <span>{customer.address?.city ?? "--"}</span>
      </div>

      <hr className="my-2" />
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <Icon path={mdiMapMarker} className="h-4 w-4 text-gray-500" />
          <span className="font-semibold text-black">Address</span>
        </div>
        <div className="text-gray-500 text-xs">
          {customer.address.line1}, {customer.address.line2},{" "}
          {customer.address.line3},{customer.address.city},{" "}
          {customer.address.state}, {customer.address.country} -{" "}
          {customer.address.pincode}
        </div>
      </div>
    </>
  );

  return (
    <>
      {addNew && (
        <ModalDialog
          id={`${id_prefix}-add-modal`}
          title="Add Billing Address"
          show={addNew}
          onClose={() => setAddNew(false)}
        >
          <div className="p-2 md:p-5">
            <AddBillingWidget
              id_prefix={`${id_prefix}-add`}
              onAdded={(customerDetail) => {
                const isDuplicate = customers.some(
                  (customer) =>
                    (customer.branch ?? "").trim().toLowerCase() ===
                    (customerDetail?.branch ?? "").trim().toLowerCase(),
                );
                if (isDuplicate) {
                  showToast({
                    type: ToastType.error,
                    text: "A billing profile with this branch name already exists.",
                  });
                  return;
                } else {
                  showToast({
                    type: ToastType.success,
                    text: "Created Successfully!",
                  });
                }
                setAddNew(false);

                const billingProfile: CustomerBillingAddressModel = {
                  branch_name: customerDetail.branch,
                  type: AddressType.billing,
                  line1: customerDetail.address.line1,
                  line2: customerDetail.address.line2 || "",
                  line3: customerDetail.address.line3 || "",
                  city: customerDetail.address.city,
                  state: customerDetail.address.state,
                  country: customerDetail.address.country,
                  pincode: customerDetail.address.pincode,
                };

                setCustomers((prev) => [...prev, customerDetail]);

                if (onBillingAddressAdded) {
                  onBillingAddressAdded(billingProfile);
                }

                setPage((prevPage) =>
                  Math.ceil((customers.length + 1) / (isMobile ? 1 : 2)),
                );
              }}
              uid={uid}
              registered_address={register_address}
              setRegisteredAddress={setRegisteredAddress}
              type={AddressType.billing}
            />
          </div>
        </ModalDialog>
      )}

      {editProfile && (
        <ModalDialog
          id={`${id_prefix}-edit-modal`}
          title="Edit Billing Address"
          show={!!editProfile}
          onClose={() => setEditProfile(null)}
        >
          <div className="p-2 md:p-5">
            <AddBillingWidget
              id_prefix={`${id_prefix}-edit`}
              key={editProfile.branch}
              editProfile={editProfile}
              onAdded={() => {}}
              onEdited={(customerDetail) => {
                setEditProfile(null);

                const billingProfile: CustomerBillingAddressModel = {
                  branch_name: customerDetail.branch,
                  type: AddressType.billing,
                  line1: customerDetail.address.line1,
                  line2: customerDetail.address.line2 || "",
                  line3: customerDetail.address.line3 || "",
                  city: customerDetail.address.city,
                  state: customerDetail.address.state,
                  country: customerDetail.address.country,
                  pincode: customerDetail.address.pincode,
                };

                setCustomers((prev) =>
                  prev.map((c) =>
                    (c.branch ?? "").trim().toLowerCase() ===
                    (customerDetail.branch ?? "").trim().toLowerCase()
                      ? customerDetail
                      : c,
                  ),
                );

                if (onBillingAddressUpdated) {
                  onBillingAddressUpdated(billingProfile);
                }
              }}
              uid={uid}
              registered_address={register_address}
              setRegisteredAddress={setRegisteredAddress}
              type={AddressType.billing}
            />
          </div>
        </ModalDialog>
      )}

      {!viewOnly && customers.length === 0 && (
        <div className="hidden md:flex justify-end gap-2 items-end my-2 mt-5">
          <button
            id={`${id_prefix}-add-new-btn`}
            onClick={() => {
              setAddNew(true);
            }}
            className="cursor-pointer text-white bg-myPrimaryColor px-3 py-1 rounded font-semibold"
          >
            Add new
          </button>
        </div>
      )}

      {viewOnly ? (
        <>
          {/* Mobile: horizontal swipe carousel */}
          <div className="md:hidden min-w-0">
            <div
              className="flex flex-nowrap gap-3 w-full max-w-full overflow-x-auto snap-x snap-mandatory pb-2 text-xs"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {customers.map((customer, i) => (
                <div
                  key={i}
                  style={{ flex: "0 0 85%" }}
                  className="snap-start bg-white rounded-lg border p-3 shadow-sm"
                >
                  {renderCardBody(customer)}
                </div>
              ))}
            </div>
          </div>

          {/* Desktop grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-5 w-full">
            {customers.map((customer, i) => (
              <div
                key={i}
                className="bg-white h-60 w-full p-5 rounded-lg border text-xs"
              >
                {renderCardBody(customer)}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-5 w-full">
          {customers.map((customer, i) => (
            <div
              key={i}
              id={`${id_prefix}-card-${i}`}
              className="relative bg-white hover:bg-green-50 w-full p-5 rounded-lg border text-xs"
            >
              <div className="absolute top-2 right-2 flex items-center gap-3">
                <button
                  id={`${id_prefix}-card-${i}-edit-btn`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditProfile(customer);
                  }}
                  className="text-xs text-myPrimaryColor font-semibold underline"
                >
                  Edit
                </button>
                <button
                  id={`${id_prefix}-card-${i}-delete-btn`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setToDelete(customer);
                  }}
                  className="text-xs text-red-500 font-semibold underline"
                >
                  Delete
                </button>
              </div>
              {renderCardBody(customer)}
            </div>
          ))}
        </div>
      )}

      {toDelete && (
        <ModalDialog
          id={`${id_prefix}-delete-modal`}
          title="Delete Billing Address"
          show={!!toDelete}
          onClose={() => setToDelete(null)}
        >
          <div className="p-4 flex flex-col gap-4">
            <div className="flex gap-3">
              <Icon
                path={mdiAlertCircleOutline}
                className="h-6 w-6 text-red-500 shrink-0"
              />
              <div className="text-sm text-gray-700">
                <p className="font-semibold mb-1">
                  Are you sure you want to delete this billing address?
                </p>
                <p>
                  Billing address{" "}
                  <span className="font-semibold">
                    {toDelete.branch || "—"}
                  </span>{" "}
                  will be permanently deleted. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                id={`${id_prefix}-delete-cancel-btn`}
                type="button"
                onClick={() => setToDelete(null)}
                className="px-3 py-1 border rounded text-sm"
              >
                Cancel
              </button>
              <button
                id={`${id_prefix}-delete-confirm-btn`}
                type="button"
                onClick={() => {
                  const profile = toDelete;
                  setToDelete(null);
                  handleDelete(profile);
                }}
                className="px-3 py-1 bg-red-500 text-white rounded text-sm font-semibold"
              >
                Delete Address
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
    </>
  );
};

export default NewBillingWidget;
